/**
 * React Query hooks for Earthworks EW-5 (fieldiq-api#1290): field tickets and
 * change orders, office (camelCase) and /field (snake_case).
 *
 * Same rules as useEarthworks: every key starts ['earthworks'], every write
 * invalidates that one prefix (a sign-off moves the job's lists, the register
 * and the record together), and every read spreads LOAD_FAILURE_QUERY_OPTIONS
 * so a refusal reads as a refusal, never as an empty list.
 *
 * These routes send no `meta`; a screen that needs the zone or units reads
 * useEwMeta / useTenantTimeZone.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { LOAD_FAILURE_QUERY_OPTIONS } from '../lib/load-failure';
import type {
  EwChangeOrder,
  EwChangeOrderDetail,
  EwCoSent,
  EwCoWrite,
  EwFieldChangeOrder,
  EwFieldTicket,
  EwPaperSignWrite,
  EwPhoneSignWrite,
  EwTicket,
  EwTicketDetail,
  EwTicketFilters,
  EwTicketLineWrite,
} from '../types/earthworks';
import { invalidateEw } from './useEarthworks';

/** `data` from an envelope; `data: null` (or a missing key) stays null — never the envelope. */
function dataOf<T>(body: unknown): T | null {
  if (body === null || typeof body !== 'object' || Array.isArray(body)) return null;
  const b = body as { data?: unknown };
  return 'data' in b && b.data !== undefined && b.data !== null ? (b.data as T) : null;
}
function listOf<T>(body: unknown): T[] {
  const d = dataOf<unknown>(body);
  return Array.isArray(d) ? (d as T[]) : [];
}

const clean = (p?: object) => {
  if (!p) return {};
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(p)) if (v !== undefined && v !== null && v !== '') out[k] = String(v);
  return out;
};

export const ewTicketKeys = {
  tickets: (p?: EwTicketFilters) => ['earthworks', 'tickets', clean(p)] as const,
  ticket: (id: string) => ['earthworks', 'ticket', id] as const,
  changeOrders: (p?: { job_id?: string; status?: string }) => ['earthworks', 'changeOrders', clean(p)] as const,
  changeOrder: (id: string) => ['earthworks', 'changeOrder', id] as const,
  fieldTickets: (jobId: string) => ['earthworks', 'field', 'tickets', jobId] as const,
  fieldTicket: (id: string) => ['earthworks', 'field', 'ticket', id] as const,
  fieldChangeOrders: (jobId: string) => ['earthworks', 'field', 'changeOrders', jobId] as const,
  fieldChangeOrder: (id: string) => ['earthworks', 'field', 'changeOrder', id] as const,
};

function useEwWrite<V, R>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => invalidateEw(qc) });
}

// ─── Office: tickets ─────────────────────────────────────────────────────────

/** The register. `total` is the API's (the list is paginated), null when the body did not carry one. */
export function useEwTickets(filters?: EwTicketFilters, enabled = true) {
  return useQuery({
    queryKey: ewTicketKeys.tickets(filters),
    queryFn: async () => {
      const body = (await api.earthworks.tickets.list(clean(filters) as EwTicketFilters)).data as unknown;
      const total = (body as { total?: unknown } | null)?.total;
      return { data: listOf<EwTicket>(body), total: typeof total === 'number' ? total : null };
    },
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useEwTicket(id: string | undefined) {
  return useQuery({
    queryKey: ewTicketKeys.ticket(id ?? ''),
    queryFn: async () => dataOf<EwTicketDetail>((await api.earthworks.tickets.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useCreateEwTicket = () =>
  useEwWrite(async ({ jobId, workDate }: { jobId: string; workDate?: string }) => {
    const res = await api.earthworks.tickets.create(jobId, workDate ? { work_date: workDate } : {});
    return { data: dataOf<EwTicketDetail>(res.data), built: res.data?.built ?? null };
  });
export const useRebuildEwTicket = () =>
  useEwWrite(async (id: string) => {
    const res = await api.earthworks.tickets.rebuild(id);
    return { data: dataOf<EwTicketDetail>(res.data), built: res.data?.built ?? null };
  });
export const useUpdateEwTicketNotes = () =>
  useEwWrite(async ({ id, notes }: { id: string; notes: string | null }) => (await api.earthworks.tickets.update(id, { notes })).data);
export const useAddEwTicketLine = () =>
  useEwWrite(async ({ id, body }: { id: string; body: EwTicketLineWrite }) => (await api.earthworks.tickets.addLine(id, body)).data);
export const useUpdateEwTicketLine = () =>
  useEwWrite(async ({ id, lineId, body }: { id: string; lineId: string; body: EwTicketLineWrite }) =>
    (await api.earthworks.tickets.updateLine(id, lineId, body)).data);
export const useRemoveEwTicketLine = () =>
  useEwWrite(async ({ id, lineId }: { id: string; lineId: string }) => (await api.earthworks.tickets.removeLine(id, lineId)).data);
export const usePaperSignEwTicket = () =>
  useEwWrite(async ({ id, body }: { id: string; body: EwPaperSignWrite }) => (await api.earthworks.tickets.paper(id, body)).data);
export const useAttachEwSignedCopy = () =>
  useEwWrite(async ({ id, body }: { id: string; body: { upload_id: string; notes?: string } }) =>
    (await api.earthworks.tickets.signedCopy(id, body)).data);
export const useApproveEwTicket = () => useEwWrite(async (id: string) => (await api.earthworks.tickets.approve(id)).data);
export const useVoidEwTicket = () =>
  useEwWrite(async ({ id, reason }: { id: string; reason: string }) => (await api.earthworks.tickets.void(id, { reason })).data);

// ─── Office: change orders ───────────────────────────────────────────────────

export function useEwChangeOrders(filters?: { job_id?: string; status?: string }, enabled = true) {
  return useQuery({
    queryKey: ewTicketKeys.changeOrders(filters),
    queryFn: async () => listOf<EwChangeOrder>((await api.earthworks.changeOrders.list(clean(filters))).data),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useEwChangeOrder(id: string | undefined) {
  return useQuery({
    queryKey: ewTicketKeys.changeOrder(id ?? ''),
    queryFn: async () => dataOf<EwChangeOrderDetail>((await api.earthworks.changeOrders.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useCreateEwChangeOrder = () =>
  useEwWrite(async ({ jobId, body }: { jobId: string; body: EwCoWrite }) =>
    dataOf<EwChangeOrderDetail>((await api.earthworks.changeOrders.create(jobId, body)).data));
export const useUpdateEwChangeOrder = () =>
  useEwWrite(async ({ id, body }: { id: string; body: EwCoWrite }) => (await api.earthworks.changeOrders.update(id, body)).data);
export const useAddEwCoLine = () =>
  useEwWrite(async ({ id, body }: { id: string; body: EwTicketLineWrite }) => (await api.earthworks.changeOrders.addLine(id, body)).data);
export const useUpdateEwCoLine = () =>
  useEwWrite(async ({ id, lineId, body }: { id: string; lineId: string; body: EwTicketLineWrite }) =>
    (await api.earthworks.changeOrders.updateLine(id, lineId, body)).data);
export const useRemoveEwCoLine = () =>
  useEwWrite(async ({ id, lineId }: { id: string; lineId: string }) => (await api.earthworks.changeOrders.removeLine(id, lineId)).data);
export const useSendEwChangeOrder = () =>
  useEwWrite(async ({ id, email }: { id: string; email?: string }): Promise<EwCoSent> =>
    (await api.earthworks.changeOrders.send(id, email ? { email } : {})).data);
export const useVoidEwChangeOrder = () =>
  useEwWrite(async ({ id, reason }: { id: string; reason: string }) => (await api.earthworks.changeOrders.void(id, { reason })).data);

// ─── /field (snake_case) ─────────────────────────────────────────────────────

export function useEwFieldTickets(jobId: string | undefined) {
  return useQuery({
    queryKey: ewTicketKeys.fieldTickets(jobId ?? ''),
    queryFn: async () => listOf<EwFieldTicket>((await api.earthworks.field.tickets(jobId as string)).data),
    enabled: !!jobId,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useEwFieldTicket(id: string | undefined) {
  return useQuery({
    queryKey: ewTicketKeys.fieldTicket(id ?? ''),
    queryFn: async () => dataOf<EwFieldTicket>((await api.earthworks.field.ticket(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useCreateEwFieldTicket = () =>
  useEwWrite(async (jobId: string) => dataOf<EwFieldTicket>((await api.earthworks.field.createTicket(jobId, {})).data));
export const useSignEwFieldTicket = () =>
  useEwWrite(async ({ id, body }: { id: string; body: EwPhoneSignWrite }) => (await api.earthworks.field.signTicket(id, body)).data);
export const usePaperEwFieldTicket = () =>
  useEwWrite(async ({ id, body }: { id: string; body: EwPaperSignWrite }) => (await api.earthworks.field.paperTicket(id, body)).data);

export function useEwFieldChangeOrders(jobId: string | undefined) {
  return useQuery({
    queryKey: ewTicketKeys.fieldChangeOrders(jobId ?? ''),
    queryFn: async () => listOf<EwFieldChangeOrder>((await api.earthworks.field.changeOrders(jobId as string)).data),
    enabled: !!jobId,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useEwFieldChangeOrder(id: string | undefined) {
  return useQuery({
    queryKey: ewTicketKeys.fieldChangeOrder(id ?? ''),
    queryFn: async () => dataOf<EwFieldChangeOrder>((await api.earthworks.field.changeOrder(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useSignEwFieldChangeOrder = () =>
  useEwWrite(async ({ id, body }: { id: string; body: EwPhoneSignWrite }) => (await api.earthworks.field.signChangeOrder(id, body)).data);
